import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShieldAlert, CalendarCheck, Clock, MessageSquare, Inbox, Star, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { BookingRow, InquiryRow } from '../components/admin/AdminRows';
import { OfferBannerForm } from '../components/admin/OfferBannerForm';
import { ReviewsList } from '../components/admin/ReviewsList';
import { BookingCalendar } from '../components/admin/BookingCalendar';
import { CurrencyRatesForm, TravelBookingsAdmin } from '../components/admin/TravelAndCurrency';
import { BrandingForm } from '../components/admin/BrandingForm';

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <Card className="border-0 shadow-md">
    <CardContent className="p-5 flex items-center gap-4">
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900" data-testid={testId}>{value}</p>
      </div>
    </CardContent>
  </Card>
);

const EmptyState = ({ text }) => (
  <Card className="border-0 shadow-sm"><CardContent className="p-12 text-center text-gray-500">
    <Inbox className="w-12 h-12 mx-auto mb-3 text-gray-300" />{text}
  </CardContent></Card>
);

const Admin = ({ user, authChecked }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [travel, setTravel] = useState([]);
  const [stats, setStats] = useState(null);

  const refreshStats = () => api.get('/admin/stats').then((s) => setStats(s.data)).catch(() => {});

  const load = useCallback(async () => {
    try {
      const [b, i, r, s, t] = await Promise.all([
        api.get('/admin/bookings'), api.get('/admin/inquiries'), api.get('/admin/reviews'), api.get('/admin/stats'), api.get('/admin/travel-bookings'),
      ]);
      setBookings(b.data); setInquiries(i.data); setReviews(r.data); setStats(s.data); setTravel(t.data);
    } catch (e) {
      toast.error('Failed to load admin data');
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  const replaceBooking = (updated) => setBookings((prev) => prev.map((b) => (b.booking_id === updated.booking_id ? updated : b)));

  const updateBooking = async (id, status) => {
    try {
      const res = await api.patch(`/admin/bookings/${id}/status`, { status });
      replaceBooking(res.data);
      toast.success(`Booking marked ${status}${status !== 'pending' ? ' — traveler notified by email' : ''}`);
      refreshStats();
    } catch (e) {
      toast.error('Failed to update booking');
    }
  };

  const replaceInquiry = (updated) => {
    setInquiries((prev) => prev.map((q) => (q.inquiry_id === updated.inquiry_id ? updated : q)));
    refreshStats();
  };

  const updateInquiry = async (id, status) => {
    try {
      const res = await api.patch(`/admin/inquiries/${id}/status`, { status });
      replaceInquiry(res.data);
      toast.success(`Inquiry marked ${status}`);
    } catch (e) {
      toast.error('Failed to update inquiry');
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center pt-16"><p className="text-gray-600">Loading...</p></div>;
  }

  if (!user || !user.is_admin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16 px-4">
        <Card className="border-0 shadow-xl max-w-md w-full" data-testid="admin-access-denied">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin access only</h2>
            <p className="text-gray-600 mb-6">
              {user ? `${user.email} is not an admin account.` : 'Please sign in with the TripSoul admin Google account.'}
            </p>
            <Button data-testid="admin-login-btn" onClick={() => navigate(user ? '/' : '/login')} className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
              {user ? 'Back to Home' : 'Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16" data-testid="admin-page">
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-2">Admin Panel</h1>
          <p className="text-teal-50 text-lg">Manage bookings, messages, reviews and homepage offers</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={CalendarCheck} label="Total bookings" value={stats.bookings_total} testId="stat-bookings-total" />
            <StatCard icon={Clock} label="Pending" value={stats.bookings_pending} testId="stat-bookings-pending" />
            <StatCard icon={CalendarCheck} label="Confirmed" value={stats.bookings_confirmed} testId="stat-bookings-confirmed" />
            <StatCard icon={MessageSquare} label="New messages" value={stats.inquiries_new} testId="stat-inquiries-new" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="h-12 flex-wrap">
            <TabsTrigger data-testid="admin-tab-bookings" value="bookings" className="text-base px-5">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-travel" value="travel" className="text-base px-5">Flights & Hotels ({travel.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-calendar" value="calendar" className="text-base px-5"><CalendarDays className="w-4 h-4 mr-1" />Calendar</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-inquiries" value="inquiries" className="text-base px-5">Messages ({inquiries.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-reviews" value="reviews" className="text-base px-5"><Star className="w-4 h-4 mr-1" />Reviews ({reviews.length})</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-offers" value="offers" className="text-base px-5">Offer Banner</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-currencies" value="currencies" className="text-base px-5">Currencies</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-branding" value="branding" className="text-base px-5">Logo</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'bookings' && (
          <div className="space-y-3" data-testid="admin-bookings-list">
            {bookings.length === 0 ? <EmptyState text="No booking inquiries yet" />
              : bookings.map((b) => <BookingRow key={b.booking_id} b={b} onStatus={updateBooking} onSaved={replaceBooking} />)}
          </div>
        )}

        {tab === 'calendar' && <BookingCalendar bookings={bookings} />}

        {tab === 'inquiries' && (
          <div className="space-y-3" data-testid="admin-inquiries-list">
            {inquiries.length === 0 ? <EmptyState text="No contact messages yet" />
              : inquiries.map((q) => <InquiryRow key={q.inquiry_id} q={q} onStatus={updateInquiry} onReplied={replaceInquiry} />)}
          </div>
        )}

        {tab === 'reviews' && (
          <ReviewsList reviews={reviews} onDeleted={(id) => setReviews((prev) => prev.filter((r) => r.review_id !== id))} />
        )}

        {tab === 'offers' && <OfferBannerForm />}
        {tab === 'currencies' && <CurrencyRatesForm />}
        {tab === 'branding' && <BrandingForm />}
        {tab === 'travel' && <TravelBookingsAdmin bookings={travel} onUpdated={(u) => setTravel((prev) => prev.map((b) => (b.booking_id === u.booking_id ? u : b)))} />}
      </div>
    </div>
  );
};

export default Admin;
